<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { formatTime } from '@/utils/datetime'
import { toast } from '@/components/ui/toast'
import { Tabs } from '@/components/ui/tabs'
import { TabsList } from '@/components/ui/tabs'
import { TabsTrigger } from '@/components/ui/tabs'
import { TabsContent } from '@/components/ui/tabs'
import { Slider } from '@/components/ui/slider'
import DetailCollectedData from './DetailCollectedData.vue'
import DetailPlanetOrder from './DetailPlanetOrder.vue'
import LayerFeaturesList from '@/components/layers/variants/LayerFeaturesList.vue'

const route = useRoute()
const store = useStore()
const orgId = computed(() => route.params.org_id)
const layerId = computed(() => route.params.layer_id)
const activeTab = ref("data")
const opacity = ref(100)
const map = computed(() => store.getters['map/getMap'])

const layer = computed(() => {
  return store.getters['layers/getOrgLayerById'](orgId.value, layerId.value)
})

const isCollectedDataLayer = computed(() => {
  return layer.value?.properties?.type === 4
})

const isPlanetOrderLayer = computed(() => {
  return layer.value?.properties?.type === 1
})

const isStaticLayer = computed(() => {
  return layer.value?.properties?.type === 5
})

const isLayerWithFeatures = computed(() => {
  return isCollectedDataLayer.value || isStaticLayer.value
})

const layerCode = computed(() => {
  return layer.value?.uuid
})

const settings = computed(() => store.getters['user/getUserSettings'])

const getActiveTabKey = computed(() => {
  return `layer_${layerId.value}_active_tab`
})



const onOpacityChange = (value) => {
  opacity.value = value[0]

  if (!map.value || !layerId.value) return

  try {
    const layers = map.value.getStyle().layers.filter((layer) =>
      layer.metadata?.groupId === layerId.value
    )

    layers.forEach((layer) => {
      if (layer.type === 'fill') {
        map.value.setPaintProperty(
          layer.id,
          'fill-opacity',
          opacity.value / 100
        )
      } else if (layer.type === 'line') {
        map.value.setPaintProperty(
          layer.id,
          'line-opacity',
          opacity.value / 100
        )
      } else if (layer.type === 'circle') {
        map.value.setPaintProperty(
          layer.id,
          'circle-opacity',
          opacity.value / 100
        )
      } else if (layer.type === 'symbol') {
        map.value.setPaintProperty(
          layer.id,
          'text-opacity',
          opacity.value / 100
        )
      } else if (layer.type === 'raster') {
        map.value.setPaintProperty(
          layer.id,
          'raster-opacity',
          opacity.value / 100
        )
      }
    })

    const updatedLayer = {
      ...layer.value,
      properties: {
        ...layer.value.properties,
        // Preserve existing features data when updating opacity
        ...(layer.value?.properties?.features ? {
          features: layer.value.properties.features,
          featureCollection: layer.value.properties.featureCollection
        } : {}),
        opacity: opacity.value / 100
      }
    }

    store.dispatch('layers/updateOrgLayer', {
      orgId: orgId.value,
      layer: updatedLayer
    })
  } catch (error) {
    console.error('Error updating layer opacity:', error)
    toast({
      title: 'Update failed',
      description: 'Failed to update layer opacity',
      variant: 'destructive'
    })
  }
}

watch(activeTab, (newTab) => {
  if (layerId.value) {
    localStorage.setItem(getActiveTabKey.value, newTab)
  }
})

onMounted(() => {
  if (layer.value?.properties?.opacity !== undefined) {
    opacity.value = Math.round(layer.value.properties.opacity * 100)
  }

  if (layerId.value) {
    const savedTab = localStorage.getItem(getActiveTabKey.value)
    if (savedTab) {
      activeTab.value = savedTab
    }
  }
})

// watch([layerId, orgId], fetchLayerData)
</script>

<template>
  <div class="p-3" v-if="layer">
    <div class="flex justify-between items-center">
      <h3 class="text-lg font-bold">{{ layer.properties.name || 'Unnamed' }}</h3>
    </div>
    <ul class="text-gray-500">
      <li><span>Created at: </span> {{ formatTime(layer?.properties?.created_at, settings.tz, 'D MMMM YYYY HH:mm') }}</li>
      <li v-if="layer?.properties?.updated_at !== layer?.properties?.created_at">Updated at: {{ formatTime(layer?.properties?.updated_at, settings.tz, 'D MMMM YYYY HH:mm') }}</li>
    </ul>

    <div class="mt-4">
      <Tabs v-model="activeTab" default-value="data" class="w-full">
        <TabsList class="grid grid-cols-3">
          <TabsTrigger value="data">Data</TabsTrigger>
          <TabsTrigger value="filter">Filter</TabsTrigger>
          <TabsTrigger v-if="isCollectedDataLayer" value="ai-reports">AI Reports</TabsTrigger>
          <!-- <TabsTrigger value="style">Style</TabsTrigger> -->
        </TabsList>

        <div class="mt-4">
          <TabsContent value="data">
            <!-- Collected Data Layer (type 4) -->
            <DetailCollectedData
              v-if="isCollectedDataLayer"
              :layer="layer"
              :activeTab="'data'"
            />

            <!-- Planet Order Layer (type 1) -->
            <DetailPlanetOrder
              v-else-if="isPlanetOrderLayer"
              :layer="layer"
              :activeTab="'data'"
            />

            <!-- Static Layer (type 5) -->
            <div v-else-if="isStaticLayer">
              <LayerFeaturesList :code="layerCode" />
            </div>

            <!-- Other layer types -->
            <p v-else class="text-sm text-muted-foreground">No additional data available for this layer</p>
          </TabsContent>

          <TabsContent value="ai-reports" v-if="isCollectedDataLayer">
            <DetailCollectedData
              :layer="layer"
              :activeTab="'ai-reports'"
            />
          </TabsContent>

          <TabsContent value="style">
            <div class="flex items-center justify-between space-x-4">
              <h4 class="text-sm font-medium">Opacity</h4>
              <Slider
                :default-value="[opacity]"
                :max="100"
                :step="1"
                class="w-full max-w-md"
                @update:model-value="onOpacityChange($event)"
              />
              <span class="text-sm">
                {{ opacity }}%
              </span>
            </div>
          </TabsContent>

          <TabsContent value="filter">
            <!-- Collected Data Layer Filters (type 4) -->
            <DetailCollectedData
              v-if="isCollectedDataLayer"
              :layer="layer"
              :activeTab="'filter'"
            />

            <!-- Planet Order Layer Filters (type 1) -->
            <DetailPlanetOrder
              v-else-if="isPlanetOrderLayer"
              :layer="layer"
              :activeTab="'filter'"
            />

            <!-- Other layer types -->
            <p v-else class="text-sm text-muted-foreground">This layer doesn't support filters</p>
          </TabsContent>
        </div>

      </Tabs>
    </div>
  </div>
</template>
